import {groupByUniqueKey} from '@benchling/js-utils';
import gql from 'graphql-tag';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {refreshStudyItemListings} from 'app-core/platform/listable/refreshListings';
import {pluralize} from 'BenchlingUtil';
import {COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeTestOrderBulkEventEventMetadata,
  TEST_ORDER_BULK_CREATE_ENDED_EVENT,
  TEST_ORDER_BULK_CREATE_STARTED_EVENT,
} from 'performance/processSampleTestsEvents';
import CreateTestOrdersModal from 'sample-tests/ui/test-orders/modals/CreateTestOrdersModal';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';

import {createTestOrders} from '../../../network/TestOrderDAO';
import {
  CreateTestOrdersButtonQuery,
  CreateTestOrdersButtonQueryVariables,
} from './__generated/CreateTestOrdersButtonQuery';

type BioentityWithTrialSampleInfo = ExcludeInaccessible<CreateTestOrdersButtonQuery['bioentities'][number]>;

export const TRIAL_SAMPLE_QUERY = gql`
  query CreateTestOrdersButtonQuery($sampleIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: SAMPLE_TESTS) {
    bioentities(ids: $sampleIds) {
      ... on RegistryItemInterface {
        id
        name
        isTrialSample
        archiveRecord {
          purpose
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export async function handleOnSubmit(
  testOrders: Array<TestOrderCreateInfo>,
  studyIds: Array<string>
): Promise<void> {
  logProfilerTimingEvent(TEST_ORDER_BULK_CREATE_STARTED_EVENT);
  await createTestOrders(testOrders, {actionOrigin: ActionOrigin.SAMPLE_TESTS_CREATE_TEST_ORDERS_BUTTON});
  const numTestOrders = testOrders.length;
  notifications.success(`Successfully created ${numTestOrders} test ${pluralize(numTestOrders, 'order')}`);
  refreshStudyItemListings(studyIds);
  logProfilerTimingEvent(
    TEST_ORDER_BULK_CREATE_ENDED_EVENT,
    composeTestOrderBulkEventEventMetadata(numTestOrders, ActionOrigin.SAMPLE_TESTS_CREATE_TEST_ORDERS_BUTTON)
  );
}

export interface CreateTestOrdersButtonProps {
  sampleIds: Array<string> | null;
  studyIds: Array<string>;
}

export interface TestOrderCreateInfo {
  sampleId: string;
  testDefinitionId: string;
  containerId: string | null;
  dilutionFactor: number | null;
  studyIds: Array<string>;
  procedureId: string;
}

async function getTrialSampleInfosForSampleIds(sampleIds: Array<string>): Promise<{
  [key: string]: BioentityWithTrialSampleInfo;
}> {
  const data = await doQuerySafe<CreateTestOrdersButtonQuery, CreateTestOrdersButtonQueryVariables>(
    {query: TRIAL_SAMPLE_QUERY},
    {variables: {sampleIds}}
  );

  const accessibleBioentities: Array<BioentityWithTrialSampleInfo> = accessibleObjs(data.bioentities);
  const sampleInfos = groupByUniqueKey(accessibleBioentities, (bioentity) => bioentity.id);
  return sampleInfos;
}

interface SampleIdsToSampleInfos {
  [key: string]: BioentityWithTrialSampleInfo;
}

function useFetchSampleInfosState(
  sampleIds: Array<string>,
  setLoading: (loading: boolean) => void
): SampleIdsToSampleInfos {
  const [sampleInfos, setSampleInfos] = useState<SampleIdsToSampleInfos>({});

  useEffect(() => {
    async function fetchSampleInfos(): Promise<void> {
      setLoading(true);
      const filteredSampleIds = sampleIds.filter((sampleId) => APIUtil.isBiologicalEntity(sampleId));
      const sampleInfos = await getTrialSampleInfosForSampleIds(filteredSampleIds);
      setSampleInfos(sampleInfos);
      setLoading(false);
    }
    fetchSampleInfos();
  }, [sampleIds, setLoading]);

  return sampleInfos;
}

export default function CreateTestOrdersButton(props: CreateTestOrdersButtonProps): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [disabledTooltipText, setDisabledTooltipText] = useState<string | null>(null);
  const sampleInfos = useFetchSampleInfosState(props.sampleIds ?? [], setLoading);

  useEffect(() => {
    const updateDisabledTooltipText = (sampleInfos: SampleIdsToSampleInfos): void => {
      const sampleInfosArray = Object.values(sampleInfos);
      const hasAllTrialSamples = sampleInfosArray.every((sampleInfo) => sampleInfo.isTrialSample);
      const hasTrialSample = sampleInfosArray.some((sampleInfo) => sampleInfo.isTrialSample);
      const hasOnlyUnarchivedTrialSamples = sampleInfosArray.every((sampleInfo) => !sampleInfo.archiveRecord);

      let disabledTooltipText: string | null = null;
      if (props.studyIds.length === 0) {
        disabledTooltipText = 'Please select a study to create test orders';
      } else if (!hasAllTrialSamples) {
        const nonTrialSampleNames = sampleInfosArray
          .filter((sampleInfo) => !sampleInfo.isTrialSample)
          .map((sampleInfo) => sampleInfo.name);
        const truncatedJoinedNonTrialSamplesNamesWithEllipsis =
          nonTrialSampleNames.length <= 3
            ? nonTrialSampleNames.join(', ')
            : `${nonTrialSampleNames.slice(0, 3).join(', ')}, ...`;
        disabledTooltipText = `Test orders can only be created for trial samples. These are not trial samples: ${truncatedJoinedNonTrialSamplesNamesWithEllipsis}`;
      } else if (!hasTrialSample) {
        disabledTooltipText = 'Select trial samples to create test orders';
      } else if (!hasOnlyUnarchivedTrialSamples) {
        const archivedTrialSampleNames = sampleInfosArray
          .filter((sampleInfo) => sampleInfo.archiveRecord)
          .map((sampleInfo) => sampleInfo.name);
        const truncatedJoinedArchivedTrialSamplesNamesWithEllipsis =
          archivedTrialSampleNames.length <= 3
            ? archivedTrialSampleNames.join(', ')
            : `${archivedTrialSampleNames.slice(0, 3).join(', ')}, ...`;
        disabledTooltipText = `Test orders can not be created for archived trial samples. Unarchive ${truncatedJoinedArchivedTrialSamplesNamesWithEllipsis} to create test orders.`;
      }
      setDisabledTooltipText(disabledTooltipText);
    };
    updateDisabledTooltipText(sampleInfos);
  }, [props.studyIds, sampleInfos]);

  const canCreateTestOrders = disabledTooltipText == null;

  const handleCreateTestOrders = async (): Promise<void> => {
    if (canCreateTestOrders) {
      analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.SAMPLE_TESTS, {
        modalType: 'create_test_orders',
        numSamples: Object.keys(sampleInfos).length,
      });
      await legacyRunModal(CreateTestOrdersModal, {
        sampleIds: Object.keys(sampleInfos),
        studyIds: props.studyIds,
        onRequestSubmit: (testOrders) => handleOnSubmit(testOrders, props.studyIds),
      });
    }
  };

  return (
    <Tooltip isDisabled={false} text={disabledTooltipText}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        fullWidth={false}
        text="Create test orders"
        onClick={handleCreateTestOrders}
        disabled={loading || !canCreateTestOrders}
        dataTestAttrs={{component: 'CreateTestOrdersButton', element: 'root'}}
      />
    </Tooltip>
  );
}
